/**
 * OrderMatchMap — Phase 43.8 (riscrittura UX-first)
 *
 * Mostra al driver, in modo semplice e operativo, se l'ordine rientra nel suo
 * tragitto abituale + deviazione massima. Niente schemi tecnici A/B/P/D:
 *
 *   • Se Google Maps è disponibile → mappa Static reale con percorso ordine
 *     blu e tragitto abituale grigio scuro (linee dirette geodesic, semplici).
 *   • Se Maps non è disponibile → card testuale chiara con metriche operative
 *     + CTA "Apri in Google Maps".
 *
 * Compatibilità con label/colori coerenti (no contraddizioni):
 *   80-100 → Ottima compatibilità   (emerald)
 *   60-79  → Buona compatibilità    (teal)
 *   40-59  → Compatibilità marginale (amber)
 *    0-39  → Poco compatibile       (rose)
 *
 * Props:
 *  - pickupLat, pickupLng, deliveryLat, deliveryLng
 *  - routeStartLat, routeStartLng, routeEndLat, routeEndLng
 *  - maxDetourKm (number)
 *  - matchedRouteName (string)
 *  - usualTimeWindow (object | null)
 *  - className (string, opz.)
 */
import { useState } from "react";
import { Target, ExternalLink, MapPin } from "lucide-react";
import { getBrowserMapsKey } from "@/lib/maps";
import { computeMatchScore, commuteTimingLabel } from "@/lib/match_score";

const STATIC_BASE = "https://maps.googleapis.com/maps/api/staticmap";

function bandFor(score) {
  if (score >= 80) return { label: "Ottima compatibilità", tone: "emerald" };
  if (score >= 60) return { label: "Buona compatibilità", tone: "teal" };
  if (score >= 40) return { label: "Compatibilità marginale", tone: "amber" };
  return { label: "Poco compatibile", tone: "rose" };
}

const TONE_CLASSES = {
  emerald: { card: "border-emerald-200 bg-emerald-50/60", text: "text-emerald-900", muted: "text-emerald-800/80", badge: "bg-emerald-600 text-white", bar: "bg-emerald-500" },
  teal:    { card: "border-teal-200 bg-teal-50/60",       text: "text-teal-900",    muted: "text-teal-800/80",    badge: "bg-teal-600 text-white",    bar: "bg-teal-500" },
  amber:   { card: "border-amber-200 bg-amber-50/70",     text: "text-amber-900",   muted: "text-amber-800/80",   badge: "bg-amber-600 text-white",   bar: "bg-amber-500" },
  rose:    { card: "border-rose-200 bg-rose-50/60",       text: "text-rose-900",    muted: "text-rose-800/80",    badge: "bg-rose-600 text-white",    bar: "bg-rose-500" },
};

function buildStaticUrl({
  pickupLat, pickupLng, deliveryLat, deliveryLng,
  routeStartLat, routeStartLng, routeEndLat, routeEndLng,
  key, width, height,
}) {
  const p = new URLSearchParams();
  p.set("size", `${width}x${height}`);
  p.set("scale", "2");
  p.set("maptype", "roadmap");
  p.set("language", "it");
  p.set("region", "CH");
  p.set("key", key);
  // Tragitto abituale del driver (grigio scuro)
  p.append("path", `color:0x09090Bcc|weight:4|geodesic:true|${routeStartLat},${routeStartLng}|${routeEndLat},${routeEndLng}`);
  // Ordine ritiro → consegna (blu)
  p.append("path", `color:0x0052FFee|weight:5|geodesic:true|${pickupLat},${pickupLng}|${deliveryLat},${deliveryLng}`);
  // Marker semplici (no lettere): ritiro blu, consegna rosso
  p.append("markers", `color:0x0052FF|${pickupLat},${pickupLng}`);
  p.append("markers", `color:0xDC2626|${deliveryLat},${deliveryLng}`);
  return `${STATIC_BASE}?${p.toString()}`;
}

function gmapsDeepLink({ pickupLat, pickupLng, deliveryLat, deliveryLng }) {
  if (!Number.isFinite(pickupLat) || !Number.isFinite(deliveryLat)) return null;
  return `https://www.google.com/maps/dir/?api=1&origin=${pickupLat},${pickupLng}&destination=${deliveryLat},${deliveryLng}&travelmode=driving`;
}

export default function OrderMatchMap({
  pickupLat, pickupLng,
  deliveryLat, deliveryLng,
  routeStartLat, routeStartLng,
  routeEndLat, routeEndLng,
  maxDetourKm = 5,
  matchedRouteName = "",
  usualTimeWindow = null,
  className = "",
}) {
  const [imgFailed, setImgFailed] = useState(false);

  const hasOrderCoords = Number.isFinite(pickupLat) && Number.isFinite(pickupLng) && Number.isFinite(deliveryLat) && Number.isFinite(deliveryLng);
  const hasRouteCoords = Number.isFinite(routeStartLat) && Number.isFinite(routeStartLng) && Number.isFinite(routeEndLat) && Number.isFinite(routeEndLng);
  const mapsKey = getBrowserMapsKey();
  const canShowMap = hasOrderCoords && hasRouteCoords && mapsKey && !imgFailed;

  const scoreInfo = computeMatchScore({
    pickupLat, pickupLng, deliveryLat, deliveryLng,
    routeStartLat, routeStartLng, routeEndLat, routeEndLng,
    maxDetourKm,
  });
  const score = scoreInfo?.score ?? null;
  const detour = scoreInfo?.detourDeltaKm;
  const withinDetour = scoreInfo ? (detour <= maxDetourKm) : true;
  const band = score != null ? bandFor(score) : { label: "Compatibile con il tuo tragitto", tone: "emerald" };
  const tone = TONE_CLASSES[band.tone];
  const timingLabel = commuteTimingLabel(usualTimeWindow);
  const deepLink = gmapsDeepLink({ pickupLat, pickupLng, deliveryLat, deliveryLng });

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border ${tone.card} ${className}`}
      data-testid="order-match-map"
    >
      {/* Header */}
      <div className="px-3 sm:px-4 pt-3 pb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-[10px] uppercase tracking-wider font-bold ${tone.muted}`}>
            Compatibilità tragitto
          </p>
          <p className={`text-sm font-bold tracking-tight mt-0.5 ${tone.text}`} data-testid="order-match-score-line">
            {score != null ? <>{score}% · {band.label}</> : band.label}
          </p>
          {scoreInfo && (
            <p className={`text-xs mt-0.5 ${tone.muted}`}>
              Deviazione stimata <strong>+{detour} km</strong>
              {withinDetour ? <> · rientra nella tua deviazione massima di {maxDetourKm} km</> : <> · oltre la tua deviazione massima di {maxDetourKm} km</>}
            </p>
          )}
          {(timingLabel || matchedRouteName) && (
            <p className={`text-[11px] mt-0.5 ${tone.muted}`}>
              {timingLabel || `Sul tragitto ${matchedRouteName || "abituale"}`}
            </p>
          )}
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm ${tone.badge}`}
          data-testid="order-match-badge"
        >
          <Target className="h-3 w-3" /> {score != null ? `${score}%` : "OK"}
        </span>
      </div>

      {/* Progress bar compatibilità */}
      {score != null && (
        <div className="px-3 sm:px-4">
          <div className="h-1.5 rounded-full bg-white/70 overflow-hidden">
            <div
              className={`h-full ${tone.bar} transition-all`}
              style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
              data-testid="order-match-progress"
            />
          </div>
        </div>
      )}

      {/* Mappa Static vera (se possibile) — altrimenti card testuale */}
      {canShowMap ? (
        <div className="mt-3">
          <img
            src={buildStaticUrl({
              pickupLat, pickupLng, deliveryLat, deliveryLng,
              routeStartLat, routeStartLng, routeEndLat, routeEndLng,
              key: mapsKey, width: 700, height: 200,
            })}
            alt="Sovrapposizione ordine con il tuo tragitto abituale"
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="w-full h-[180px] object-cover"
            data-testid="order-match-map-img"
          />
          <div className="px-3 sm:px-4 py-2 flex items-center gap-3 text-[11px]">
            <span className="inline-flex items-center gap-1.5 text-dapnow-ink/80">
              <span className="inline-block h-1 w-3 rounded-full bg-dapnow-ink" /> Il tuo tragitto
            </span>
            <span className="inline-flex items-center gap-1.5 text-dapnow-ink/80">
              <span className="inline-block h-1 w-3 rounded-full bg-dapnow-blue" /> Ordine
            </span>
            {deepLink && (
              <a
                href={deepLink}
                target="_blank"
                rel="noreferrer"
                data-testid="order-match-open-gmaps"
                className="ml-auto inline-flex items-center gap-1 text-dapnow-blue font-semibold hover:underline"
              >
                Apri in Google Maps <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      ) : (
        <TextOnlyCard
          tone={tone}
          detour={detour}
          maxDetourKm={maxDetourKm}
          withinDetour={withinDetour}
          deepLink={deepLink}
          mapsKey={mapsKey}
          imgFailed={imgFailed}
        />
      )}
    </div>
  );
}

function TextOnlyCard({ tone, detour, maxDetourKm, withinDetour, deepLink, mapsKey, imgFailed }) {
  const reason = !mapsKey
    ? "Mappa non disponibile"
    : imgFailed
      ? "Mappa temporaneamente non disponibile"
      : "Mappa non disponibile";
  return (
    <div className="px-3 sm:px-4 pt-2 pb-3 space-y-2" data-testid="order-match-textonly">
      <div className="rounded-xl bg-white/70 border border-white/0 px-3 py-2.5">
        <p className={`text-[11px] uppercase tracking-wider font-bold ${tone.muted}`}>{reason}</p>
        {Number.isFinite(detour) && (
          <p className={`text-sm mt-1 ${tone.text}`}>
            Deviazione stimata: <strong>+{detour} km</strong>
          </p>
        )}
        <p className={`text-xs mt-0.5 ${tone.muted}`}>
          {withinDetour
            ? <>Questo ordine <strong>rientra</strong> nella tua deviazione massima di {maxDetourKm} km.</>
            : <>Questo ordine <strong>supera</strong> la tua deviazione massima di {maxDetourKm} km.</>}
        </p>
      </div>
      {deepLink && (
        <a
          href={deepLink}
          target="_blank"
          rel="noreferrer"
          data-testid="order-match-open-gmaps-fallback"
          className="inline-flex items-center gap-1.5 rounded-full bg-dapnow-ink text-white text-xs font-bold px-3.5 py-1.5 hover:bg-dapnow-blue transition"
        >
          <MapPin className="h-3 w-3" /> Apri in Google Maps
        </a>
      )}
    </div>
  );
}
